import { useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';
import { Trash } from 'react-bootstrap-icons';

import type {
  CreateWeeklyAvailabilityInput,
  UpdateWeeklyAvailabilityInput,
  WeeklyAvailability,
} from '@repo/frontend-shared';
import {
  CreateWeeklyAvailabilityDocument,
  DeleteScheduleDocument,
  GetWeeklyAvailabilityDocument,
  UpdateWeeklyAvailabilityDocument,
} from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextInput } from '@repo/sds/SDSTextInput';
import { toast } from '@repo/sds/utils/toast';
import { UiProvider } from '@repo/ui/contexts/ui-provider';
import { DayAvailability } from '@repo/ui/fixtures/mentorAvailability/components/DayAvailability/DayAvailability';
import { DAYS_OF_WEEK } from '@repo/ui/fixtures/mentorAvailability/constants/time';
import { AvailabilityProvider } from '@repo/ui/fixtures/mentorAvailability/contexts/AvailabilityContext';
import { useAvailability } from '@repo/ui/fixtures/mentorAvailability/hooks/use-availability';
import type { DayAvailability as DayAvailabilityType } from '@repo/ui/fixtures/mentorAvailability/types/time';
import { TimezoneSelect } from '@repo/ui/fixtures/timezoneSelect/components/TimezoneSelect/TimezoneSelect';
import { backendToFrontend, frontendToBackend } from '@repo/ui/utils/availability-transforms';

import './availability-edit-modal.scss';

type AvailabilityState = Record<string, DayAvailabilityType>;

interface AvailabilityEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduleId: string | null;
  onSaveSuccess: () => void;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
}: ConfirmationModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    await onConfirm().finally(() => setIsDeleting(false));
  };

  return (
    <SDSDialog isOpen={isOpen} onClose={onClose} title={title} size="small" showCloseButton={true}>
      <SDSFlexbox direction="column" gap="no-design-16">
        <SDSText type="body-medium" text={message} />
        <SDSFlexbox direction="row" gap="button-medium" justifyContent="flex-end">
          <SDSButton
            type="button"
            onClick={onClose}
            isDisabled={isDeleting}
            text={cancelText}
            mode="outline"
            size="medium"
          />
          <SDSButton
            type="button"
            onClick={handleConfirm}
            isDisabled={isDeleting}
            text={isDeleting ? 'Deleting...' : confirmText}
            mode="outline"
            status="alert"
            size="medium"
          />
        </SDSFlexbox>
      </SDSFlexbox>
    </SDSDialog>
  );
}

function AvailabilityGrid() {
  return (
    <SDSFlexbox direction="column">
      {DAYS_OF_WEEK.map(day => (
        <div key={day} className="AvailabilityModal__dayWrapper">
          <DayAvailability day={day} />
        </div>
      ))}
    </SDSFlexbox>
  );
}

function SaveButton({
  onSave,
  onCancel,
  onDelete,
  isCreate,
  deleting,
}: {
  onSave: (availability: AvailabilityState) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => void;
  isCreate: boolean;
  deleting: boolean;
}) {
  const { availability } = useAvailability();
  const disabled = !Object.values(availability).some(day => day.isAvailable && day.timeRanges.length > 0);

  return (
    <SDSFlexbox
      direction="row"
      justifyContent={isCreate ? 'flex-end' : 'space-between'}
      alignItems="center"
      className="AvailabilityModal__buttonRow"
    >
      {!isCreate && (
        <SDSButton
          type="button"
          onClick={onDelete}
          isDisabled={deleting}
          text={deleting ? 'Deleting...' : 'Delete'}
          leftIcon={<SDSIcon icon={<Trash />} />}
          mode="outline"
          status="alert"
          size="medium"
        />
      )}
      <SDSFlexbox direction="row" gap="button-medium" className="AvailabilityModal__buttonGroup">
        <SDSButton type="button" onClick={onCancel} text="Cancel" mode="outline" size="medium" />
        <SDSButton
          type="button"
          onClick={() => onSave(availability)}
          isDisabled={disabled}
          text="Save"
          mode="primary"
          size="medium"
        />
      </SDSFlexbox>
    </SDSFlexbox>
  );
}

function AvailabilityEditModalContent({
  scheduleId,
  existing,
  onClose,
  onSaveSuccess,
}: {
  scheduleId: string | null;
  existing: WeeklyAvailability | undefined;
  onClose: () => void;
  onSaveSuccess: () => void;
}) {
  const isCreate = scheduleId === 'new';
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [createSchedule] = useMutation<{ createWeeklyAvailability: { id: string } }>(CreateWeeklyAvailabilityDocument);
  const [updateSchedule] = useMutation<{ updateWeeklyAvailability: { id: string } }>(UpdateWeeklyAvailabilityDocument);

  const [deleteSchedule, { loading: deleting }] = useMutation<{ deleteWeeklyAvailability: { id: string } }>(
    DeleteScheduleDocument,
  );

  const [title, setTitle] = useState(() => existing?.title ?? (isCreate ? 'Working Hours' : ''));
  const [timezone, setTimezone] = useState(() => existing?.timezone ?? 'UTC');

  const onSave = async (availability: AvailabilityState) => {
    try {
      if (isCreate) {
        const input: CreateWeeklyAvailabilityInput = {
          title,
          timezone,
          settings: frontendToBackend(availability),
          description: title,
          isPrimary: false,
        };

        await createSchedule({ variables: { input } });
      } else if (existing) {
        const input: UpdateWeeklyAvailabilityInput = {
          title,
          timezone,
          settings: frontendToBackend(availability),
          description: title,
        };

        await updateSchedule({ variables: { id: existing.id, input } });
      }
      toast.success('Schedule saved');
      onSaveSuccess();
      onClose();
    } catch {
      toast.error('Failed to save schedule');
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!existing) return;

    try {
      await deleteSchedule({ variables: { id: existing.id } });
      toast.success('Schedule deleted');
      onSaveSuccess();
      setShowDeleteConfirm(false);
      onClose();
    } catch {
      toast.error('Failed to delete schedule');
    }
  };

  return (
    <>
      <SDSFlexbox direction="column" gap="no-design-4">
        <SDSFlexbox direction="column" gap="no-design-4">
          <SDSTextInput
            value={title}
            type="body-medium-bold"
            onChange={value => setTitle(value)}
            placeholder="e.g. Working Hours"
            label="Name"
            dataTestId="availability-name-input"
          />
        </SDSFlexbox>

        <SDSFlexbox direction="column" gap="no-design-4" className="AvailabilityModal__timezoneSection">
          <SDSText type="body-medium-bold" text="Timezone" />
          <TimezoneSelect
            updateTimezoneContext={false}
            defaultTimezone={timezone}
            onSelectTimezone={tz => setTimezone(tz.iana)}
          />
        </SDSFlexbox>

        <AvailabilityGrid />

        <SaveButton
          onSave={onSave}
          onCancel={onClose}
          onDelete={handleDelete}
          isCreate={isCreate}
          deleting={deleting}
        />
      </SDSFlexbox>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Schedule"
        message="Are you sure you want to delete this schedule? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}

export function AvailabilityEditModal({ isOpen, onClose, scheduleId, onSaveSuccess }: AvailabilityEditModalProps) {
  const isCreate = scheduleId === 'new';

  const { data } = useQuery<{ weeklyAvailability: WeeklyAvailability }>(GetWeeklyAvailabilityDocument, {
    skip: isCreate || !scheduleId,
    variables: { id: scheduleId as string },
  });

  const existing = data?.weeklyAvailability;

  const initialAvailability: AvailabilityState | undefined = existing
    ? backendToFrontend(existing.settings)
    : undefined;

  return (
    <SDSDialog
      isOpen={isOpen}
      onClose={onClose}
      title={isCreate ? 'Add a new schedule' : 'Edit schedule'}
      size="medium"
      showCloseButton={true}
      className="AvailabilityEditModal"
    >
      <UiProvider>
        <AvailabilityProvider
          initialAvailability={initialAvailability}
          key={existing?.id ?? (isCreate ? 'new' : 'loading')}
        >
          <AvailabilityEditModalContent
            key={existing?.id ?? (isCreate ? 'new' : 'loading')}
            scheduleId={scheduleId}
            existing={existing}
            onClose={onClose}
            onSaveSuccess={onSaveSuccess}
          />
        </AvailabilityProvider>
      </UiProvider>
    </SDSDialog>
  );
}
